import { useMemo } from 'react';
import {
  getInternalAsset,
  isLegacyChainflipAsset,
  type ChainflipAsset,
} from '@chainflip/utils/chainflip';
import { isNotNullish } from '@chainflip/utils/guard';
import { isLiquidityProvisionApp } from '@/shared/featureFlags';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { useOraclePrices } from '@/shared/hooks/useOraclePrice';
import useRpcRequest from '@/shared/hooks/useRpcRequest';
import { LENDING_POOLS_APY_QUERY, LP_LENDING_POSITIONS_QUERY } from '@/shared/utils/lending';
import TokenAmount from '@/shared/utils/TokenAmount';
import { useLendingPools } from './useLendingPools';

export type LpLendingPosition = {
  totalAmount: TokenAmount;
  totalAvailableAmount: TokenAmount;
  totalUnavailableAmount: TokenAmount;
  poolSharePercent: number;
  projectedApy: number;
  idSs58: `cF${string}`;
};

export const useLpLendingPositions = (variables?: { asset?: ChainflipAsset; idSs58?: string }) => {
  const prices = useOraclePrices();

  // RPC data source (used for liquidity-provision app)
  const accountRpcResult = useRpcRequest({
    method: 'cf_account_info',
    args: [variables?.idSs58 ?? ''],
    refetchInterval: 6_000,
    enabled: isLiquidityProvisionApp && Boolean(variables?.idSs58),
  });

  const { poolBalances, isLoading: rpcLendingPoolsLoading } = useLendingPools();

  // GraphQL cache data source (used for other apps)
  const { data: cacheData, isLoading: isCacheLoading } = useGqlQuery(LP_LENDING_POSITIONS_QUERY, {
    context: { clientName: 'statechainCache' },
    variables,
    refetchInterval: 6_000,
    enabled: !isLiquidityProvisionApp,
  });

  const { data: apyData, isLoading: apyLoading } = useGqlQuery(LENDING_POOLS_APY_QUERY, {
    context: { clientName: 'lpProcessor' },
    variables: variables?.asset && { asset: variables.asset },
    refetchInterval: 30_000,
  });

  const rpcProcessedData = useMemo(() => {
    if (!isLiquidityProvisionApp) return undefined;

    const accountRpcData =
      accountRpcResult.data?.role === 'liquidity_provider'
        ? accountRpcResult.data.lending_positions
        : undefined;

    if (!accountRpcData || !variables?.idSs58 || !apyData?.lendingPools?.nodes) {
      return [];
    }

    const apyPoolByAsset = new Map<ChainflipAsset, (typeof apyData.lendingPools.nodes)[number]>();
    for (const pool of apyData.lendingPools.nodes) {
      const asset = pool.stats.nodes.at(0)?.asset;
      if (asset && !isLegacyChainflipAsset(asset)) apyPoolByAsset.set(asset, pool);
    }

    return accountRpcData
      .map((position) => {
        const chainflipAsset = getInternalAsset({
          chain: position.chain,
          asset: position.asset,
        });

        if (variables?.asset && variables.asset !== chainflipAsset) {
          return null;
        }

        const pool = apyPoolByAsset.get(chainflipAsset);
        if (!pool) return null;

        const usdPrice = prices?.[chainflipAsset]?.usdPrice ?? 0;

        const lpTotalAmount = TokenAmount.fromAsset(position.total_amount, chainflipAsset);
        const lpTotalAmountUsd = lpTotalAmount.toUsdNumber(usdPrice);

        const lpAvailableAmount = TokenAmount.fromAsset(position.available_amount, chainflipAsset);
        const lpAvailableAmountUsd = lpAvailableAmount.toUsdNumber(usdPrice);

        const poolTotalAmount = poolBalances?.[chainflipAsset]?.totalAmount ?? TokenAmount.ZERO;

        const poolSharePercent = poolTotalAmount.eq(0)
          ? 0
          : lpTotalAmount
              .toBigNumber()
              .div(poolTotalAmount.toBigNumber())
              .multipliedBy(100)
              .toNumber();

        return {
          lpIdSs58: variables.idSs58,
          asset: chainflipAsset,
          totalAmount: lpTotalAmount.toString(),
          totalAmountUsd: lpTotalAmountUsd.toFixed(30),
          totalAvailableAmount: lpAvailableAmount.toString(),
          totalAvailableAmountUsd: lpAvailableAmountUsd.toFixed(30),
          poolSharePercent,
        };
      })
      .filter(isNotNullish);
  }, [accountRpcResult.data, poolBalances, apyData, prices, variables?.asset, variables?.idSs58]);

  const processedData = isLiquidityProvisionApp
    ? (rpcProcessedData ?? [])
    : (cacheData?.lpPositions?.nodes ?? []);

  const totalSupplied = useMemo(
    () => processedData.reduce((acc, position) => acc + Number(position.totalAmountUsd), 0),
    [processedData],
  );

  return {
    data: processedData.map((node) => ({
      ...node,
      projectedApy: Number(
        apyData?.lendingPools?.nodes
          .find((pool) => pool.stats.nodes.at(0)?.asset === node.asset)
          ?.stats.nodes.at(0)?.projectedApy168H ?? 0,
      ),
    })),

    lpPositions: processedData.reduce(
      (acc, position) => {
        const amounts = {
          totalAmount: TokenAmount.fromAsset(position.totalAmount, position.asset),
          totalAvailableAmount: TokenAmount.fromAsset(
            position.totalAvailableAmount,
            position.asset,
          ),
          poolSharePercent: position.poolSharePercent,
        };

        acc[position.asset] = {
          ...amounts,
          idSs58: position.lpIdSs58 as `cF${string}`,
          totalUnavailableAmount: amounts.totalAmount
            .sub(amounts.totalAvailableAmount)
            .max(TokenAmount.ZERO),
          projectedApy: Number(
            apyData?.lendingPools?.nodes
              .find((pool) => pool.stats.nodes.at(0)?.asset === position.asset)
              ?.stats.nodes.at(0)?.projectedApy168H ?? 0,
          ),
        };

        return acc;
      },
      {} as Record<ChainflipAsset, LpLendingPosition>,
    ),
    positionAssets: processedData.map((p) => p.asset),
    weightedAverageApy:
      totalSupplied === 0
        ? 0
        : (apyData?.lendingPools?.nodes.reduce((acc, pool) => {
            const asset = pool.stats.nodes.at(0)?.asset;
            const position = processedData.find((p) => p.asset === asset);
            if (!position) return acc;
            const apy = Number(pool.stats.nodes.at(0)?.projectedApy168H ?? 0);
            return acc + (Number(position.totalAmountUsd) / totalSupplied) * apy;
          }, 0) ?? 0),

    isLoading: isLiquidityProvisionApp
      ? rpcLendingPoolsLoading || accountRpcResult.isLoading || apyLoading || !prices
      : isCacheLoading || apyLoading,
  };
};
